import React, { useEffect, useRef, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import getContext from 'react-recompose/getContext';
import styles from './styles.less';
import { ChatEmptyState, StarterChips } from './ChatEmptyState.tsx';
import { ChatContext, Starter } from './types.ts';

const PUBLIC_URL = process.env.PUBLIC_URL;

// Client side fallback timeout for a df preview fetch. Only fires when no reply
// arrives at all (a dropped socket): the server already errors at its own 15s
// timeout, so this is kept above that. Frontend timeouts are plain consts here,
// not env vars (env vars in this bundle are build time only, not runtime tunable).
const DF_CLIENT_TIMEOUT_MS = 20000;
const louieIcon = `${PUBLIC_URL}/img/louie.png`;

interface RunNode {
    node_type: string;
    id: string;
    state: string;
    run_type: string;
    action?: { expression?: string };
    children?: string[];
}

interface ChatElement {
    type: string;
    id: string;
    metadata?: {
        shape?: [number, number];
        columns?: Record<string, string>;
        summary?: Record<string, unknown>;
        status?: string;
    };
}

interface ChatMessage {
    id: number;
    role: 'user' | 'assistant' | 'error' | 'thinking';
    content: string;
    responseId?: number;
    runNodes?: { [id: string]: RunNode };
    elements?: ChatElement[];
    runStartTime?: number;
    runDuration?: number;
}

interface Socket {
    on(event: string, cb: (data: any) => void): void;
    off(event: string, cb: (data: any) => void): void;
    emit(event: string, data?: any): void;
}

interface ChatWidgetProps {
    socket?: Socket;
    enabled?: boolean;
    isOpen?: boolean;
    inputText?: string;
    isLoading?: boolean;
    currentResponseId?: number;
    runStartTime?: number;
    streamingMode?: boolean;
    messages?: ChatMessage[];
    runNodes?: { [id: string]: RunNode };
    context?: ChatContext | null;
    starters?: Starter[] | null;
    startersError?: boolean;
    activeSkill?: string | null;
    onToggleOpen: (props?: any) => void;
    onClose: (props?: any) => void;
    onSetInput: (props: any) => void;
    onSendMessage: (props: any) => void;
    onReceiveStart: (props?: any) => void;
    onReceiveChunk: (props: any) => void;
    onReceiveThinking: (props: any) => void;
    onPromoteThinking: (props?: any) => void;
    onReceiveDone: (props?: any) => void;
    onReceiveError: (props: any) => void;
    onReceiveRunUpdate: (props: any) => void;
    onReceiveElement: (props: any) => void;
    onClearContext: (props?: any) => void;
    onReceiveContext: (props: any) => void;
    onReceiveStarters: (props: any) => void;
    onReceiveSkill: (props?: any) => void;
    onStopChat: (props?: any) => void;
    onSetStreamingMode: (props: any) => void;
}

function ChatWidgetInner({
    socket,
    isOpen = false,
    inputText = '',
    isLoading = false,
    currentResponseId = 0,
    runStartTime = 0,
    streamingMode = true,
    messages = [],
    runNodes = {},
    context = null,
    starters = null,
    startersError = false,
    activeSkill = null,
    onToggleOpen,
    onClose,
    onSetInput,
    onSendMessage,
    onReceiveStart,
    onReceiveChunk,
    onReceiveThinking,
    onPromoteThinking,
    onReceiveDone,
    onReceiveError,
    onReceiveRunUpdate,
    onReceiveElement,
    onClearContext,
    onReceiveContext,
    onReceiveStarters,
    onReceiveSkill,
    onStopChat,
    onSetStreamingMode,
}: ChatWidgetProps) {
    const [louieConnected, setLouieConnected] = useState<boolean | null>(null);
    const [skillList, setSkillList] = useState<Array<{ id: string; name: string; description: string }>>([]);
    const [showSkillList, setShowSkillList] = useState(false);
    const [dthreadId, setDthreadId] = useState<string | null>(null);
    const [nowTick, setNowTick] = useState(() => Date.now());
    const [collapsedElements, setCollapsedElements] = useState<Set<string>>(() => new Set());

    const toggleElement = useCallback((id: string) => {
        setCollapsedElements((prev) => {
            const next = new Set(prev);
            if (next.has(id)) { next.delete(id); } else { next.add(id); }
            return next;
        });
    }, []);

    type DfState = { columns: string[]; rows: Record<string, unknown>[]; numRows: number } | 'loading' | 'error';
    const [dfData, setDfData] = useState<Record<string, DfState>>({});
    const requestedDfRef = useRef<Set<string>>(new Set());

    const requestDf = useCallback((id: string) => {
        if (requestedDfRef.current.has(id)) return;
        requestedDfRef.current.add(id);
        setDfData((prev) => ({ ...prev, [id]: 'loading' }));
        if (socket) socket.emit('chat:fetch_df', { elementId: id });
        // If no reply ever arrives (dropped socket), fail the chip instead of hanging on
        // Loading, and free the id so Retry can re-request.
        setTimeout(() => {
            setDfData((prev) => {
                if (prev[id] !== 'loading') return prev;
                requestedDfRef.current.delete(id);
                return { ...prev, [id]: 'error' };
            });
        }, DF_CLIENT_TIMEOUT_MS);
    }, [socket]);

    useEffect(() => {
        if (!socket) return;
        const onDf = (d: any) => {
            if (d.error) requestedDfRef.current.delete(d.elementId); // free the id so Retry can re-request
            setDfData((prev) => ({
                ...prev,
                [d.elementId]: d.error ? 'error' : { columns: Array.isArray(d.columns) ? d.columns : [], rows: Array.isArray(d.rows) ? d.rows : [], numRows: d.numRows || 0 },
            }));
        };
        socket.on('chat:df_data', onDf);
        return () => socket.off('chat:df_data', onDf);
    }, [socket]);

    useEffect(() => {
        for (const msg of messages) {
            for (const el of ((msg as ChatMessage).elements || [])) {
                if (el.type === 'DfElement' && el.metadata && el.metadata.columns && Object.keys(el.metadata.columns).length) {
                    requestDf(el.id);
                }
            }
        }
    }, [messages, requestDf]);

    const renderElementChip = useCallback((el: ChatElement) => {
        const cols = el.metadata && el.metadata.columns;
        const hasCols = !!(cols && Object.keys(cols).length);
        const isDf = el.type === 'DfElement';
        const openable = isDf && hasCols;
        const expanded = !collapsedElements.has(el.id);
        const shape = el.metadata && el.metadata.shape;
        const df = dfData[el.id];
        const summary: any = (el.metadata && el.metadata.summary) || {};
        return (
            <span key={el.id} className={styles.elementInline}>
                <button
                    type="button"
                    className={styles.elementChip}
                    title={df === 'error' ? 'Click to retry' : elementChipTitle(el)}
                    onClick={openable ? () => (df === 'error' ? requestDf(el.id) : toggleElement(el.id)) : undefined}
                >
                    <span className={styles.elementChipIcon}>{el.type === 'GraphElement' ? '◉' : '▦'}</span>
                    {elementChipLabel(el)}
                    {openable ? <span className={styles.elementChipCaret}>{expanded ? '▾' : '▸'}</span> : null}
                </button>
                {expanded && openable && (
                    <div className={styles.elementColumns}>
                        {df === 'loading' ? (
                            <div className={styles.dfStatus}>Loading rows…</div>
                        ) : df === 'error' ? (
                            <div className={styles.dfStatus}>Couldn’t load rows.</div>
                        ) : df && typeof df === 'object' ? (
                            <div className={styles.dfTableWrap}>
                                <div className={styles.elementColumnsTitle}>
                                    {shape ? `${shape[0]} rows × ${shape[1]} columns` : `${df.rows.length} rows`}
                                    {df.numRows > df.rows.length ? ` · showing first ${df.rows.length}` : ''}
                                </div>
                                <table className={styles.dfTable}>
                                    <thead>
                                        <tr>{df.columns.map((c) => <th key={c}>{c}</th>)}</tr>
                                    </thead>
                                    <tbody>
                                        {df.rows.map((row, i) => (
                                            <tr key={i}>{df.columns.map((c) => <td key={c}>{row[c] == null ? '' : (typeof row[c] === 'object' ? JSON.stringify(row[c]) : String(row[c]))}</td>)}</tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <>
                                <div className={styles.elementColumnsTitle}>
                                    {shape ? `${shape[0]} rows × ${shape[1]} columns` : 'columns'} · summary
                                </div>
                                {Object.entries(cols!).map(([name, dtype]) => {
                                    const s: any = summary[name];
                                    const preview = Array.isArray(s)
                                        ? s.slice(0, 10).map(String).join(', ')
                                        : (s && typeof s === 'object' && ('min' in s || 'max' in s))
                                            ? `${s.min} – ${s.max}`
                                            : String(dtype);
                                    return (
                                        <div key={name} className={styles.elementColumn}>
                                            <span className={styles.elementColName}>{name}</span>
                                            <span className={styles.elementColType}>{preview}</span>
                                        </div>
                                    );
                                })}
                            </>
                        )}
                    </div>
                )}
            </span>
        );
    }, [collapsedElements, toggleElement, dfData, requestDf]);
    const stoppedRef = useRef(false);
    const [pickedStarters, setPickedStarters] = useState<Set<string>>(() => new Set());
    const [startersCollapsed, setStartersCollapsed] = useState(false);
    const messagesPanelRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const pendingSkillsRef = useRef<Map<string, string>>(new Map()); // uploadId -> content, per in-flight upload
    const uploadIdRef = useRef(0); // monotonic per-connection upload id (no secure-context dependency)
    const streamContentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (messagesPanelRef.current) {
            messagesPanelRef.current.scrollTop = messagesPanelRef.current.scrollHeight;
        }
        if (streamContentRef.current) {
            streamContentRef.current.scrollTop = streamContentRef.current.scrollHeight;
        }
    }, [messages]);

    useEffect(() => {
        if (!isLoading) return;
        setNowTick(Date.now());
        const id = setInterval(() => setNowTick(Date.now()), 1000);
        return () => clearInterval(id);
    }, [isLoading]);

    useEffect(() => {
        if (!socket) return;

        const onChatResponse = (data: any) => {
            if (stoppedRef.current) {
                if (data.type === 'done' || data.type === 'error') {
                    stoppedRef.current = false;
                }
                return;
            }
            switch (data.type) {
                case 'start':
                    onReceiveStart();
                    break;
                case 'chunk':
                    onReceiveChunk({ content: data.content || '' });
                    break;
                case 'thinking':
                    onReceiveThinking({ content: data.content || '' });
                    break;
                case 'promote_thinking':
                    onPromoteThinking();
                    break;
                case 'run_update':
                    if (data.node) onReceiveRunUpdate({ node: data.node });
                    break;
                case 'dthread':
                    if (typeof data.dthreadId === 'string') setDthreadId(data.dthreadId);
                    break;
                case 'element':
                    if (data.element) onReceiveElement({ element: data.element });
                    break;
                case 'done':
                    onReceiveDone();
                    break;
                case 'error':
                    if (data.uploadId) pendingSkillsRef.current.delete(data.uploadId);
                    onReceiveError({ content: data.content });
                    break;
            }
        };

        const onChatStatus = (data: { connected: boolean }) => {
            setLouieConnected(data.connected);
        };

        const onChatContext = (data: any) => onReceiveContext({ context: data });

        const onChatStarters = (data: any) =>
            data && Array.isArray(data.starters)
                ? onReceiveStarters({ starters: data.starters, startersError: false })
                : onReceiveStarters({ starters: [], startersError: true });

        const onSkillAttached = (data: { name: string; uploadId?: string }) => {
            if (data.uploadId) pendingSkillsRef.current.delete(data.uploadId);
            onReceiveSkill({ name: data.name });
        };

        const onSkillCleared = () => onReceiveSkill({ name: null });

        const onSkillsList = (data: any) => {
            if (data && Array.isArray(data.skills)) setSkillList(data.skills);
        };

        const onSkillConflict = (data: { name: string; uploadId: string }) => {
            const content = pendingSkillsRef.current.get(data.uploadId);
            if (content === undefined) return; // conflict for an upload with no live pending entry — ignore
            pendingSkillsRef.current.delete(data.uploadId);
            const replace = window.confirm(
                `A skill named "${data.name}" already exists in your organization — it may belong to a teammate, and replacing it overwrites their version for everyone.\n\nOK = replace it.   Cancel = save under a different name.`
            );
            if (replace) {
                socket.emit('chat:skill_upload', { uploadId: data.uploadId, name: data.name, content, replace: true });
            } else {
                const newName = window.prompt('Save the skill under a different name:', data.name);
                if (newName && newName.trim()) {
                    pendingSkillsRef.current.set(data.uploadId, content); // reuse the upload id so a re-conflict resolves
                    socket.emit('chat:skill_upload', { uploadId: data.uploadId, name: newName.trim(), content });
                }
            }
        };

        socket.on('chat:response', onChatResponse);
        socket.on('chat:status', onChatStatus);
        socket.on('chat:context', onChatContext);
        socket.on('chat:starters', onChatStarters);
        socket.on('chat:skill_attached', onSkillAttached);
        socket.on('chat:skill_cleared', onSkillCleared);
        socket.on('chat:skills_list', onSkillsList);
        socket.on('chat:skill_conflict', onSkillConflict);
        return () => {
            socket.off('chat:response', onChatResponse);
            socket.off('chat:status', onChatStatus);
            socket.off('chat:context', onChatContext);
            socket.off('chat:starters', onChatStarters);
            socket.off('chat:skill_attached', onSkillAttached);
            socket.off('chat:skill_cleared', onSkillCleared);
            socket.off('chat:skills_list', onSkillsList);
            socket.off('chat:skill_conflict', onSkillConflict);
        };
    }, [socket, onReceiveStart, onReceiveChunk, onReceiveThinking, onPromoteThinking, onReceiveRunUpdate, onReceiveElement, onReceiveDone, onReceiveError, onReceiveContext, onReceiveStarters, onReceiveSkill]);

    useEffect(() => {
        if (isOpen && socket) {
            setLouieConnected(null);
            onReceiveStarters({ starters: null, startersError: false });
            socket.emit('chat:status');
            socket.emit('chat:context');
            socket.emit('chat:starters');
            socket.emit('chat:skill_active');
        }
    }, [isOpen, socket]);

    const retryStarters = useCallback(() => {
        if (!socket) return;
        onReceiveStarters({ starters: null, startersError: false });
        socket.emit('chat:starters');
    }, [socket, onReceiveStarters]);

    const sendText = useCallback((raw: string) => {
        const text = (raw || '').trim();
        if (!text || !socket || isLoading) return false;

        stoppedRef.current = false;
        onSendMessage({ text });
        socket.emit('chat:send', { message: text });
        return true;
    }, [isLoading, socket, onSendMessage]);

    const handleStarterPick = useCallback((prompt: string) => {
        if (sendText(prompt)) {
            setPickedStarters((prev) => { const next = new Set(prev); next.add(prompt); return next; });
        }
    }, [sendText]);

    const handleSend = useCallback(() => sendText(inputText), [sendText, inputText]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }, [handleSend]);

    const handleClear = useCallback(() => {
        if (!socket) return;
        setPickedStarters(new Set());
        setStartersCollapsed(false);
        setDthreadId(null);
        onClearContext();
        onReceiveSkill({ name: null });
        socket.emit('chat:clear');
    }, [socket, onClearContext, onReceiveSkill]);

    const handleAttachClick = useCallback(() => {
        if (fileInputRef.current) fileInputRef.current.click();
    }, []);

    const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files && e.target.files[0];
        if (file && socket) {
            const content = await file.text();
            const uploadId = String(++uploadIdRef.current);
            pendingSkillsRef.current.set(uploadId, content);
            socket.emit('chat:skill_upload', { uploadId, name: file.name, content });
        }
        e.target.value = '';
    }, [socket]);

    const handleRemoveSkill = useCallback(() => {
        if (!socket) return;
        onReceiveSkill({ name: null });
        socket.emit('chat:skill_clear');
    }, [socket, onReceiveSkill]);

    const handleToggleSkillList = useCallback(() => {
        if (!socket) return;
        const next = !showSkillList;
        setShowSkillList(next);
        if (next) socket.emit('chat:skills_list');
    }, [socket, showSkillList]);

    const handleSelectSkill = useCallback((skill: { id: string; name: string }) => {
        if (!socket) return;
        socket.emit('chat:skill_select', { id: skill.id, name: skill.name });
        setShowSkillList(false);
    }, [socket]);

    const handleStop = useCallback(() => {
        stoppedRef.current = true;
        onStopChat();
        if (socket) socket.emit('chat:stop');
    }, [socket, onStopChat]);

    const runElapsed = runStartTime ? Math.max(0, Math.round((nowTick - runStartTime) / 1000)) : 0;
    const showSlowHint = isLoading && runElapsed > 30;

    return (
        <React.Fragment>
            {!isOpen && (
                <button
                    className={styles.chatTogglePanel}
                    onClick={() => onToggleOpen()}
                    title="Talk2Graph"
                >
                    <img src={louieIcon} alt="Louie" style={{ width: 20, height: 20 }} />
                </button>
            )}
            {isOpen && (
                <div className={styles.chatPanel}>
                    <div className={styles.chatHeaderLight}>
                        <div className={styles.headerLeft}>
                            <span className={styles.headerTitle}>
                                <img src={louieIcon} alt="Louie" style={{ width: 16, height: 16, marginRight: 6, verticalAlign: 'middle' }} />
                                Chat with Louie
                                <span className={styles.alphaBadge}>Alpha</span>
                            </span>
                            <div className={styles.badgeRow}>
                                <div className={`${styles.contextBadge} ${louieConnected === false ? styles.contextBadgeDisconnected : ''}`}>
                                    <span className={louieConnected === false ? styles.contextDotRed : louieConnected === null ? styles.contextDotGray : styles.contextDot} />
                                    {louieConnected === null ? 'Connecting...' : louieConnected ? 'Connected' : 'Disconnected'}
                                </div>
                                {activeSkill && (
                                    <div className={styles.skillBadge}>
                                        <span className={styles.skillBadgeName} title={activeSkill}>{activeSkill}</span>
                                        <button className={styles.skillBadgeRemove} onClick={handleRemoveSkill} title="Remove skill">
                                            {'✕'}
                                        </button>
                                    </div>
                                )}
                                {dthreadId && (
                                    <button
                                        className={styles.dthreadBadge}
                                        onClick={() => navigator.clipboard && navigator.clipboard.writeText(dthreadId)}
                                        title={`Louie dthread ${dthreadId}. Click to copy the id.`}
                                    >
                                        Thread {dthreadId.slice(0, 8)}…
                                    </button>
                                )}
                                <button
                                    className={`${styles.streamToggle} ${streamingMode ? styles.streamToggleOn : ''}`}
                                    onClick={() => onSetStreamingMode({ value: !streamingMode })}
                                    title={streamingMode ? 'Streaming mode is on — reasoning expands live. Click to turn off.' : 'Streaming mode is off — reasoning stays collapsed. Click to turn on.'}
                                >
                                    <span className={styles.streamToggleDot} />
                                    Streaming
                                </button>
                            </div>
                        </div>
                        <div className={styles.headerActions}>
                            <button
                                className={styles.clearBtn}
                                onClick={handleClear}
                                disabled={messages.length === 0}
                                title="Clear conversation"
                            >
                                {'\u21BB'}
                            </button>
                            <button
                                className={styles.closeBtn}
                                onClick={() => onClose()}
                            >
                                {'\u2715'}
                            </button>
                        </div>
                    </div>
                    {messages.length > 0 && starters && starters.length > 0 && (
                        <div className={styles.starterStrip}>
                            <button
                                type="button"
                                className={styles.starterStripHeader}
                                onClick={() => setStartersCollapsed((c) => !c)}
                            >
                                <span className={styles.starterStripCaret}>{startersCollapsed ? '▸' : '▾'}</span>
                                Suggestions
                                {pickedStarters.size > 0 && <span className={styles.starterStripCount}>{pickedStarters.size} ✓</span>}
                            </button>
                            {!startersCollapsed && (
                                <StarterChips starters={starters} picked={pickedStarters} onPick={handleStarterPick} />
                            )}
                        </div>
                    )}
                    <div className={styles.messagesPanel} ref={messagesPanelRef}>
                        {messages.length === 0 && (
                            <ChatEmptyState
                                context={context}
                                starters={starters}
                                startersError={startersError}
                                onPick={handleStarterPick}
                                onRetry={retryStarters}
                                picked={pickedStarters}
                            />
                        )}
                        {messages.map((msg) => {
                            const isActiveThinking = isLoading && msg.role === 'thinking' && msg.responseId === currentResponseId;
                            if (msg.role === 'thinking') {
                                return (
                                <details
                                    key={`${msg.id}-${isActiveThinking ? 'live' : 'done'}`}
                                    className={styles.thinkingBlock}
                                    open={isActiveThinking ? streamingMode : undefined}
                                    onToggle={isActiveThinking ? (e) => onSetStreamingMode({ value: (e.currentTarget as HTMLDetailsElement).open }) : undefined}
                                >
                                    <summary className={styles.thinkingSummary}>
                                        {isActiveThinking ? <span className={styles.thinkingSpinner} /> : null}
                                        {isActiveThinking ? 'Thinking...' : 'Context'}
                                    </summary>
                                    <div
                                        className={isActiveThinking ? `${styles.thinkingContent} ${styles.streamingContent}` : styles.thinkingContent}
                                        ref={isActiveThinking ? streamContentRef : undefined}
                                    >{msg.content}</div>
                                </details>
                                );
                            }
                            const elementQueue = [...(msg.elements || [])];
                            const messageBody = msg.role === 'assistant'
                                ? formatMessageContent(msg.content, elementQueue, renderElementChip)
                                : msg.content;
                            return (
                                <div key={msg.id}>
                                    {msg.runNodes && Object.keys(msg.runNodes).length > 0 && (
                                        <div className={styles.runCard}>
                                            <div className={styles.runCardHeader}>
                                                {'\u2705'} Completed
                                                <span className={styles.runStepsBadge}>
                                                    {Object.values(msg.runNodes).filter((n: any) => n.node_type === 'MethodRun').length} steps
                                                </span>
                                                <span className={styles.runTime}>
                                                    {msg.runDuration ? Math.round(msg.runDuration / 1000) + 's' : ''}
                                                </span>
                                            </div>
                                            <div className={styles.runStepsList}>
                                                {Object.values(msg.runNodes).filter((n: any) => n.node_type === 'MethodRun').map((n: any) => (
                                                    <div key={n.id} className={styles.runStep}>
                                                        <span className={styles.runStepDot} style={{ background: n.state === 'Done' ? '#22c55e' : n.state === 'Running' ? '#3b82f6' : '#f59e0b' }} />
                                                        <span className={styles.runStepName}>{n.action?.expression || n.run_type || n.id.substring(0, 12)}</span>
                                                        <span className={`${styles.runStepStatus} ${n.state === 'Done' ? styles.statusDone : n.state === 'Failed' ? styles.statusFailed : styles.statusRunning}`}>
                                                            {n.state}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    <div className={`${styles.message} ${styles[msg.role]}`}>
                                        {messageBody}
                                        {elementQueue.length > 0 && (
                                            <div className={styles.elementChips}>{elementQueue.map(renderElementChip)}</div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {isLoading && Object.keys(runNodes).length > 0 && (
                            <div className={styles.runCard}>
                                <div className={styles.runCardHeader}>
                                    <span className={styles.thinkingSpinner} />
                                    Running
                                    <span className={styles.runStepsBadge}>
                                        {Object.values(runNodes).filter((n: any) => n.node_type === 'MethodRun').length} steps
                                    </span>
                                    <span className={styles.runTime}>
                                        {runStartTime ? runElapsed + 's' : ''}
                                    </span>
                                </div>
                                <div className={styles.runStepsList}>
                                    {Object.values(runNodes).filter((n: any) => n.node_type === 'MethodRun').map((n: any) => (
                                        <div key={n.id} className={styles.runStep}>
                                            <span className={styles.runStepDot} style={{ background: n.state === 'Done' ? '#22c55e' : n.state === 'Running' ? '#3b82f6' : '#f59e0b' }} />
                                            <span className={styles.runStepName}>{n.action?.expression || n.run_type || n.id.substring(0, 12)}</span>
                                            <span className={`${styles.runStepStatus} ${n.state === 'Done' ? styles.statusDone : n.state === 'Failed' ? styles.statusFailed : styles.statusRunning}`}>
                                                {n.state}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {isLoading && Object.keys(runNodes).length === 0 && !['assistant', 'thinking'].includes(messages[messages.length - 1]?.role) && (
                            <div className={styles.typing}>
                                <span className={styles.thinkingSpinner} />
                                Thinking...
                            </div>
                        )}
                        {showSlowHint && (
                            <div className={styles.slowHint}>Complex queries can take a minute or two — still working…</div>
                        )}
                    </div>
                    {showSkillList && (
                        <div className={styles.skillListPanel}>
                            <div className={styles.skillListHeader}>Org skills</div>
                            {skillList.length === 0 ? (
                                <div className={styles.skillListEmpty}>No skills yet. Please upload one to get started.</div>
                            ) : (
                                skillList.map((s) => (
                                    <button
                                        key={s.id}
                                        className={`${styles.skillListItem} ${s.name === activeSkill ? styles.skillListItemActive : ''}`}
                                        onClick={() => handleSelectSkill(s)}
                                        title={s.description}
                                    >
                                        <span className={styles.skillListItemName}>{s.name}</span>
                                        {s.description && <span className={styles.skillListItemDesc}>{s.description}</span>}
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                    <div className={styles.inputArea}>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".md,.markdown,.txt"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                        <button
                            className={styles.attachBtn}
                            onClick={handleToggleSkillList}
                            disabled={isLoading}
                            title="Browse org skills"
                        >
                            <i className="fa fa-list-ul" />
                        </button>
                        <button
                            className={styles.attachBtn}
                            onClick={handleAttachClick}
                            disabled={isLoading}
                            title="Attach skill"
                        >
                            <i className="fa fa-upload" />
                        </button>
                        <textarea
                            ref={inputRef}
                            className={styles.input}
                            rows={1}
                            value={inputText}
                            placeholder="Ask about your graph..."
                            disabled={isLoading}
                            onChange={(e) => onSetInput({ text: e.target.value })}
                            onKeyDown={handleKeyDown}
                        />
                        {isLoading ? (
                            <button
                                className={styles.stopBtn}
                                onClick={handleStop}
                                title="Stop response"
                            >
                                Stop
                            </button>
                        ) : (
                            <button
                                className={styles.sendBtnBlue}
                                disabled={!(inputText || '').trim()}
                                onClick={handleSend}
                            >
                                Send
                            </button>
                        )}
                    </div>
                </div>
            )}
        </React.Fragment>
    );
}

function elementChipLabel(el: ChatElement): string {
    if (el.type === 'DfElement') {
        const s = el.metadata && el.metadata.shape;
        return s ? `Table · ${s[0]}×${s[1]}` : 'Table';
    }
    if (el.type === 'GraphElement') return 'Graph';
    return typeof el.type === 'string' ? el.type.replace('Element', '') : 'Result';
}

function elementChipTitle(el: ChatElement): string {
    const cols = el.metadata && el.metadata.columns ? Object.keys(el.metadata.columns) : [];
    if (!cols.length) return el.type;
    return cols.slice(0, 12).join(', ') + (cols.length > 12 ? '…' : '');
}

function formatMessageContent(content: string, elementQueue?: ChatElement[], renderChip?: (el: ChatElement) => React.ReactNode) {
    const collapsed = content.replace(/\n{3,}/g, '\n\n');
    const parts: React.ReactNode[] = [];
    const regex = /(\[\[:?[^\]]+\]\]|Cypher:[\s\S]*?(?=\n\n|\n[A-Z]|$)|(?:CALL|MATCH|WHERE|RETURN)\s[^\n]+|tag_\w+|account_id|user_name|pagerank|community_multilevel)/g;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(collapsed)) !== null) {
        if (match.index > lastIndex) {
            parts.push(collapsed.substring(lastIndex, match.index));
        }
        if (match[0].startsWith('[[')) {
            const el = elementQueue && elementQueue.length ? elementQueue.shift() : null;
            if (el && renderChip) parts.push(renderChip(el));
        } else {
            parts.push(<code key={match.index} className={styles.inlineCode}>{match[0]}</code>);
        }
        lastIndex = regex.lastIndex;
    }
    if (lastIndex < collapsed.length) {
        parts.push(collapsed.substring(lastIndex));
    }
    return parts;
}

export const ChatWidget = getContext({
    socket: PropTypes.object,
    // recompose's getContext typing can't express that remaining ChatWidgetProps
    // come from the redux container; ts>=4 rejects the implicit widening ts3.9 allowed
})(ChatWidgetInner as React.ComponentType<{ socket: object }>);

export default ChatWidget;
